import { ifDefined } from "lit/directives/if-defined.js";
import { base, keyName } from "w3c-keyname";

export enum KeyboardCommand {
    Up = "Up",
    Down = "Down",
    Left = "Left",
    Right = "Right",
    Top = "Top",
    Bottom = "Bottom",
    PageUp = "Pgup",
    PageDown = "Pgdn",
    Next = "Next",
    Previous = "Prev",
    Enter = "Enter",
    Leave = "Leave",
    SelectLeft = "SelectLeft",
    SelectRight = "SelectRight",
    Menu = "Menu",
    Dismiss = "Dismiss",
    Search = "Search",
    Delete = "Delete",
}

export const controlKeyCommandKeymap: Record<string, KeyboardCommand> = {
    ArrowUp: KeyboardCommand.Up,
    ArrowDown: KeyboardCommand.Down,
    ArrowLeft: KeyboardCommand.Left,
    ArrowRight: KeyboardCommand.Right,

    PageUp: KeyboardCommand.PageUp,
    PageDown: KeyboardCommand.PageDown,

    Home: KeyboardCommand.Top,
    "Ctrl+a": KeyboardCommand.Top,
    End: KeyboardCommand.Bottom,
    "Ctrl+e": KeyboardCommand.Bottom,

    "Shift+ArrowLeft": KeyboardCommand.SelectLeft,
    "Shift+ArrowRight": KeyboardCommand.SelectRight,

    Enter: KeyboardCommand.Enter,
    Escape: KeyboardCommand.Dismiss,
    "Ctrl+g": KeyboardCommand.Dismiss,

    ContextMenu: KeyboardCommand.Menu,
};

export const commandKeymap: Record<string, KeyboardCommand> = {
    ...controlKeyCommandKeymap,

    w: KeyboardCommand.Previous,
    a: KeyboardCommand.Left,
    s: KeyboardCommand.Next,
    d: KeyboardCommand.Right,

    h: KeyboardCommand.Left,
    j: KeyboardCommand.Next,
    k: KeyboardCommand.Previous,
    l: KeyboardCommand.Right,

    Space: KeyboardCommand.Enter,
    Backspace: KeyboardCommand.Leave,
    Delete: KeyboardCommand.Delete,
};

const commandToKeybindings = (function () {
    const out = new Map<KeyboardCommand, Array<string>>();
    for (const [key, command] of Object.entries(commandKeymap)) {
        const keys = out.get(command);
        if (keys === undefined) {
            out.set(command, [key]);
        } else {
            keys.push(key);
        }
    }
    return out;
})();

export function keymapLookup(
    event: KeyboardEvent,
    ignoreInputKeys?: boolean,
): KeyboardCommand | undefined {
    let name = keyName(event);
    if (["Shift", "Alt", "Control", "Meta", "Hyper"].includes(name)) {
        return undefined;
    }
    const mapped = { " ": "Space" }[name];
    if (mapped !== undefined) {
        name = mapped;
    }
    if (name.length === 1) {
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        const baseName = base[event.keyCode];
        if (
            (event.shiftKey || event.altKey || event.metaKey || name.charCodeAt(0) > 127) &&
            baseName !== undefined &&
            baseName !== name
        ) {
            name = baseName;
        }
    }
    if (event.altKey) {
        name = `Alt+${name}`;
    }
    if (event.ctrlKey) {
        name = `Ctrl+${name}`;
    }
    if (event.metaKey) {
        name = `Meta+${name}`;
    }
    if (event.shiftKey) {
        name = `Shift+${name}`;
    }
    return (ignoreInputKeys === true ? controlKeyCommandKeymap : commandKeymap)[name];
}

/**
 * Given a {@link KeyboardCommand}, return a value suitable for the
 * `aria-keyshortcuts` attribute to describe the keys bound to that command.
 */
export function keyShortcuts(command: KeyboardCommand): ReturnType<typeof ifDefined> {
    return ifDefined(commandToKeybindings.get(command)?.join(" "));
}

/**
 * Given a {@link KeyboardCommand}, return a locale formatted list of keys bound
 * to that command suitable for presenting to the user in a tooltip. If no keys
 * are bound, the empty string is returned.
 */
export function keybindingList(command: KeyboardCommand): string {
    const items = commandToKeybindings.get(command);
    return items === undefined
        ? ""
        : new Intl.ListFormat(undefined, { type: "unit", style: "short" }).format(items);
}
